import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, DollarSign, Eye, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsTab = ({ token }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    email_enabled: false,
    email_to: 'orders@posterfire.com',
    notify_new_order: true,
    notify_payment_received: true,
    notify_cancellation: true,
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_from: '',
    smtp_pass: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications?limit=50`, { headers });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {} finally { setLoading(false); }
  }, [token]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notification-settings`, { headers });
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch {} finally { setSettingsLoading(false); }
  }, [token]);

  useEffect(() => { fetchNotifications(); fetchSettings(); }, [fetchNotifications, fetchSettings]);

  const markAsRead = async (id) => {
    await axios.put(`${API}/admin/notifications/${id}/read`, {}, { headers });
    fetchNotifications();
  };

  const markAllRead = async () => {
    await axios.put(`${API}/admin/notifications/read-all`, {}, { headers });
    fetchNotifications();
  };

  const deleteNotif = async (id) => {
    await axios.delete(`${API}/admin/notifications/${id}`, { headers });
    fetchNotifications();
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all notifications?')) return;
    await axios.delete(`${API}/admin/notifications/clear-all`, { headers });
    fetchNotifications();
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/notification-settings`, settings, { headers });
      setSettings(prev => ({ ...prev, ...res.data }));
      toast.success('Settings saved!');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/notification-settings/test`, {}, { headers });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  const typeIcon = (type) => {
    if (type === 'sale') return <DollarSign className="w-4 h-4 text-emerald-500" />;
    if (type === 'cancellation') return <X className="w-4 h-4 text-red-500" />;
    return <Bell className="w-4 h-4 text-blue-500" />;
  };

  const typeColor = (type) => {
    if (type === 'sale') return 'border-l-emerald-500 bg-emerald-50';
    if (type === 'cancellation') return 'border-l-red-500 bg-red-50';
    return 'border-l-blue-500 bg-blue-50';
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6" data-testid="notifications-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
          {unreadCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700 rounded-full" data-testid="unread-badge">
              {unreadCount} unread
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="mark-all-read-btn">
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={clearAll} className="text-xs text-gray-500 hover:text-red-600 font-medium" data-testid="clear-all-btn">
              Clear all
            </button>
          )}
          <button 
            onClick={() => setShowSettings(!showSettings)} 
            className={`px-3 py-1.5 text-xs font-bold rounded border transition-colors ${showSettings ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            data-testid="notification-settings-toggle"
          >
            {showSettings ? 'View Notifications' : 'Email Settings'}
          </button>
        </div>
      </div>

      {showSettings ? (
        /* ─── Email Settings Panel ─── */
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5" data-testid="notification-settings">
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div>
              <h4 className="font-bold text-gray-900">Email Notification Settings</h4>
              <p className="text-xs text-gray-500 mt-1">Configure SMTP to send order alerts to your email</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="email-enabled-toggle">
              <input type="checkbox" checked={settings.email_enabled} onChange={(e) => setSettings({...settings, email_enabled: e.target.checked})} className="sr-only peer" />
              <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
              <span className="ml-2 text-xs font-medium text-gray-600">{settings.email_enabled ? 'ON' : 'OFF'}</span>
            </label>
          </div>

          <div className="grid gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">Recipient Email</label>
              <input type="email" value={settings.email_to} onChange={(e) => setSettings({...settings, email_to: e.target.value})}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="orders@posterfire.com" data-testid="email-to-input" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={settings.notify_new_order} onChange={(e) => setSettings({...settings, notify_new_order: e.target.checked})} className="rounded" />
                <span className="text-gray-700">New Orders</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={settings.notify_payment_received} onChange={(e) => setSettings({...settings, notify_payment_received: e.target.checked})} className="rounded" />
                <span className="text-gray-700">Payments</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={settings.notify_cancellation} onChange={(e) => setSettings({...settings, notify_cancellation: e.target.checked})} className="rounded" />
                <span className="text-gray-700">Cancellations</span>
              </label>
            </div>

            <div className="pt-3 border-t border-gray-100">
              <h5 className="text-xs font-bold text-gray-600 mb-3">SMTP Configuration</h5>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">SMTP Host</label>
                  <input type="text" value={settings.smtp_host} onChange={(e) => setSettings({...settings, smtp_host: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="smtp.gmail.com" data-testid="smtp-host-input" />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">SMTP Port</label>
                  <input type="number" value={settings.smtp_port} onChange={(e) => setSettings({...settings, smtp_port: parseInt(e.target.value) || 587})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="587" data-testid="smtp-port-input" />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">SMTP Username</label>
                  <input type="text" value={settings.smtp_user} onChange={(e) => setSettings({...settings, smtp_user: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="you@gmail.com" data-testid="smtp-user-input" />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">SMTP Password</label>
                  <input type="password" value={settings.smtp_pass} onChange={(e) => setSettings({...settings, smtp_pass: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="App password" data-testid="smtp-pass-input" />
                </div>
                <div className="col-span-2">
                  <label className="block text-[11px] text-gray-500 mb-1">From Email (optional)</label>
                  <input type="email" value={settings.smtp_from} onChange={(e) => setSettings({...settings, smtp_from: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="noreply@posterfire.com" data-testid="smtp-from-input" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-3 border-t border-gray-100">
            <button onClick={testEmail} disabled={testing || !settings.email_enabled || !settings.smtp_host}
              className="px-4 py-2 text-xs font-bold border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
              data-testid="test-email-btn">
              {testing ? 'Sending...' : 'Send Test Email'}
            </button>
            <button onClick={saveSettings} disabled={saving}
              className="px-6 py-2 text-xs font-bold bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
              data-testid="save-notification-settings-btn">
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      ) : (
        /* ─── Notifications List ─── */
        <div className="space-y-2" data-testid="notifications-list">
          {notifications.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
              <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 font-medium">No notifications yet</p>
              <p className="text-xs text-gray-400 mt-1">Sales alerts and updates will appear here</p>
            </div>
          ) : (
            notifications.map(n => (
              <div key={n.id}
                className={`flex items-start gap-3 p-4 rounded-lg border-l-4 transition-all ${typeColor(n.type)} ${!n.read ? 'border border-gray-200' : 'border border-transparent opacity-70'}`}
                data-testid={`notification-${n.id}`}>
                <div className="mt-0.5">{typeIcon(n.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-bold ${!n.read ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                    {!n.read && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>}
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{n.message}</p>
                  <p className="text-[10px] text-gray-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {!n.read && (
                    <button onClick={() => markAsRead(n.id)} className="p-1 text-gray-400 hover:text-blue-600 transition-colors" title="Mark as read">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => deleteNotif(n.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Delete"
                    data-testid={`delete-notification-${n.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};


export default NotificationsTab;
